import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { storage } from "../utils/storage";

const baseUrl = process.env.REACT_APP_API_CONFIG;

export interface StripePrice {
  id: string;
  tier: "free" | "basic" | "pro";
  name: string;
  description: string | null;
  amount: number;
  currency: string;
  interval: "month" | "year";
  intervalCount: number;
}

export interface CheckoutResponse {
  type: "checkout" | "portal";
  sessionId?: string;
  url: string;
  message?: string;
}

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  tier: "free" | "basic" | "pro";
  platform?: "apple" | "google" | "stripe" | "manual";
  status?: "active" | "canceled" | "expired" | "refunded" | "paused";
  expiresAt?: string;
  autoRenewing?: boolean;
}

/**
 * Hook for Stripe subscription management
 */
export function useStripePrices() {
  const [prices, setPrices] = useState<StripePrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(`${baseUrl}/subscription/stripe/prices`);
        if (response.data.success) {
          setPrices(response.data.data);
        } else {
          setError("Failed to fetch prices");
        }
      } catch (err: any) {
        setError(err.response?.data?.error?.message || "Failed to fetch prices");
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
  }, []);

  return { prices, loading, error };
}

/**
 * Hook for Stripe checkout and portal
 */
export function useStripeCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckout = useCallback(async (priceId: string): Promise<CheckoutResponse | null> => {
    const token = storage.getJwtToken();
    if (!token) {
      setError("Please login to subscribe");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${baseUrl}/subscription/stripe/create-checkout`,
        {
          priceId,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription/cancel`,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        setError("Failed to create checkout session");
        return null;
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to create checkout");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const openCustomerPortal = useCallback(async (): Promise<string | null> => {
    const token = storage.getJwtToken();
    if (!token) {
      setError("Please login to manage subscription");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${baseUrl}/subscription/stripe/create-portal`,
        {
          returnUrl: `${window.location.origin}/profile`,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        return response.data.data.url;
      } else {
        setError("Failed to open customer portal");
        return null;
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to open portal");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createCheckout, openCustomerPortal, loading, error };
}

/**
 * Hook for subscription status
 */
export function useSubscriptionStatus() {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    const token = storage.getJwtToken();
    if (!token) {
      setStatus({ hasActiveSubscription: false, tier: "free" });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${baseUrl}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setStatus(response.data.data);
      } else {
        setStatus({ hasActiveSubscription: false, tier: "free" });
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to fetch status");
      setStatus({ hasActiveSubscription: false, tier: "free" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { status, loading, error, refetch: fetchStatus };
}
